import React from 'react';
import PropTypes from 'prop-types';
import './Card.css';

/**
 * Componente Card moderno seguindo a identidade visual Mexx
 * Suporta diferentes variantes, elevações e estados interativos
 */
const Card = ({
  children,
  variant = 'default',
  elevation = 'medium',
  padding = 'medium',
  hoverable = false,
  clickable = false,
  header = null,
  footer = null,
  className = '',
  onClick,
  ...props
}) => {
  const baseClass = 'mexx-card';
  const variantClass = `mexx-card--${variant}`;
  const elevationClass = `mexx-card--elevation-${elevation}`;
  const paddingClass = `mexx-card--padding-${padding}`;
  const hoverableClass = hoverable ? 'mexx-card--hoverable' : '';
  const clickableClass = clickable ? 'mexx-card--clickable' : '';

  const cardClass = [
    baseClass,
    variantClass,
    elevationClass,
    paddingClass,
    hoverableClass,
    clickableClass,
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (clickable && onClick) {
      onClick(e);
    }
  };

  const handleKeyDown = (e) => {
    if (clickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      if (onClick) {
        onClick(e);
      }
    }
  };

  const CardWrapper = clickable ? 'div' : 'div';

  return (
    <CardWrapper
      className={cardClass}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : undefined}
      {...props}
    >
      {/* Header */}
      {header && (
        <div className="mexx-card__header">
          {header}
        </div>
      )}

      {/* Content */}
      <div className="mexx-card__content">
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div className="mexx-card__footer">
          {footer}
        </div>
      )}
    </CardWrapper>
  );
};

/**
 * Card Header Component
 */
const CardHeader = ({
  title,
  subtitle,
  action,
  icon,
  className = '',
  ...props
}) => {
  return (
    <div className={`mexx-card__header-content ${className}`} {...props}>
      <div className="mexx-card__header-main">
        {icon && (
          <div className="mexx-card__header-icon">
            {icon}
          </div>
        )}
        <div className="mexx-card__header-text">
          {title && (
            <h3 className="mexx-card__title">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="mexx-card__subtitle">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {action && (
        <div className="mexx-card__header-action">
          {action}
        </div>
      )}
    </div>
  );
};

/**
 * Card Footer Component
 */
const CardFooter = ({
  children,
  align = 'right',
  className = '',
  ...props
}) => {
  const alignClass = `mexx-card__footer-content--${align}`;

  return (
    <div className={`mexx-card__footer-content ${alignClass} ${className}`} {...props}>
      {children}
    </div>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    'default',
    'outlined',
    'elevated',
    'success',
    'warning',
    'error',
    'info'
  ]),
  elevation: PropTypes.oneOf(['none', 'small', 'medium', 'large']),
  padding: PropTypes.oneOf(['none', 'small', 'medium', 'large']),
  hoverable: PropTypes.bool,
  clickable: PropTypes.bool,
  header: PropTypes.node,
  footer: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func
};

CardHeader.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  icon: PropTypes.node,
  className: PropTypes.string
};

CardFooter.propTypes = {
  children: PropTypes.node.isRequired,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  className: PropTypes.string
};

// Export named components
Card.Header = CardHeader;
Card.Footer = CardFooter;

export default Card;